import React, { useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import get from "lodash/get";
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { Modal } from 'react-bootstrap';
import "./scss/FormInputYesNo.scss";

interface IFormInputYesNoSwitchProps {
    name: string;
    title?: string;
    disabled?: boolean;
    col?: number;
    required?: string | null;
    label?: string;
    modal?: string | null;
}

const FormInputYesNoSwitch: React.FC<IFormInputYesNoSwitchProps> = ({
    name,
    title = "",
    disabled = false,
    col = 12,
    required = null,
    label = "",
    modal = null
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [showModal, setShowModal] = useState(false);
    const {
        setValue,
        formState: { errors },
        control,
        register
    } = useFormContext();

    const value = useWatch({ control, name });
    const errorMessage = get(errors, name)?.message as string | undefined;

    const setYes = () => {
        if (!disabled)
            setValue(name, true, { shouldValidate: true });
    };

    const setNo = () => {
        if (!disabled)
            setValue(name, false, { shouldValidate: true });
    };

    const toggle = () => {
        if (disabled) return;

        if (value === null || value === undefined) {
            setValue(name, true, { shouldValidate: true });
        } else {
            setValue(name, !value, { shouldValidate: true });
        }
    };

    const state =
        value === true ? "yes" :
            value === false ? "no" :
                "neutral";

    return (
        <div
            ref={ref}
            data-input-name={name}
            className={`col-12 col-md-${col}`}
        >

            <div className={`input-block ${errorMessage ? 'error' : ''}`}>

                {label && (
                    <div className="d-flex align-items-center mb-1">
                        <label className="input-block__label mb-0">
                            {label}
                        </label>
                        {modal && (
                            <TooltipComponent content="Más Información">
                                <div
                                    onClick={() => setShowModal(true)}
                                    className="information-icon-orange p-0 ms-2"
                                    style={{ cursor: 'pointer' }}
                                />
                            </TooltipComponent>
                        )}
                    </div>
                )}

                {errorMessage && (
                    <p className="error m-0 mb-1">{errorMessage}</p>
                )}

                <div className="yesno-container">

                    {title && (
                        <div className="d-flex align-items-center">
                            <span className="yesno-label">
                                {title}
                            </span>
                            {modal && !label && (
                                <TooltipComponent content="Más Información">
                                    <div
                                        onClick={() => setShowModal(true)}
                                        className="information-icon-orange p-0 ms-2"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </TooltipComponent>
                            )}
                        </div>
                    )}

                    <div className={`yesno-separator ${value !== undefined && value !== null ? "active" : ""}`} />

                    {/* Registro oculto */}
                    <input
                        type="hidden"
                        {...register(name, {
                            setValueAs: (val) => {
                                if (val === "" || val === undefined || val === null) return null;
                                if (val === "true") return true;
                                if (val === "false") return false;
                                return val;
                            },
                            validate: (val) => {
                                if (required && (val === undefined || val === null || val === "")) {
                                    return required;
                                }
                                return true;
                            }
                        })}
                    />

                    <div className="yesno-switch-group">

                        <span
                            className={`yesno-text ${value === false ? "active" : ""}`}
                            onClick={setNo}
                        >
                            No
                        </span>

                        <div
                            className={`yesno-switch ${state}`}
                            onClick={toggle}
                        >
                            <div className="yesno-ball" />
                        </div>

                        <span
                            className={`yesno-text ${value === true ? "active" : ""}`}
                            onClick={setYes}
                        >
                            Sí
                        </span>

                    </div>

                </div>

            </div>

            <Modal size='lg' show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{label || title || 'Información'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modal}</Modal.Body>
            </Modal>
        </div>
    );

};

export default FormInputYesNoSwitch;
